import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AuthUser, Paginated } from '@touristation/shared';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import type {
  AppRole, AuditLog, Availability, BookedTicket, Booking, CompanyEmail, CsConversation,
  CourseStats, Department, Employee, Infostaff, LearningAssignment, LearningCourse, LearningOverview,
  Lesson, Meeting, MyProfile, Notification, OfficeHandover, PriceProduct, QuizResult,
  RosterBalance, RosterPlan, Shift, ShiftLocation, ShiftPreference, ShiftType, StaffingTarget,
  Task, Ticket, TicketCategory, Tool, VaultEntryView, WikiPage,
} from './types';

const page = <T>(p: string) => () => apiGet<Paginated<T>>(p);
const list = <T>(p: string) => () => apiGet<T[]>(p);

// ── identity ──
export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => apiGet<AuthUser>('/auth/me') });

// ── settings (custom logo, etc.) ──
export const useLogo = () =>
  useQuery({ queryKey: ['setting', 'logo'], queryFn: () => apiGet<{ key: string; value: string | null }>('/settings/logo'), staleTime: 5 * 60_000 });
export function useSetSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => apiPut(`/settings/${key}`, { value }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['setting', v.key] }),
  });
}

// ── ops ──
export const useTools = () => useQuery({ queryKey: ['tools'], queryFn: page<Tool>('/tools?pageSize=100') });
export interface ToolInput { name: string; category: string; href: string; description?: string; icon?: string; color?: string; kind?: string; tag?: string }
export function useCreateTool() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: ToolInput) => apiPost<Tool>('/tools', body), onSuccess: () => qc.invalidateQueries({ queryKey: ['tools'] }) });
}
export function useUpdateTool() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, ...body }: Partial<ToolInput> & { id: string }) => apiPatch<Tool>(`/tools/${id}`, body), onSuccess: () => qc.invalidateQueries({ queryKey: ['tools'] }) });
}
export function useDeleteTool() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/tools/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['tools'] }) });
}
export const usePriceList = () => useQuery({ queryKey: ['price-list'], queryFn: page<PriceProduct>('/price-list?pageSize=100') });
export const useBookings = () => useQuery({ queryKey: ['bookings'], queryFn: page<Booking>('/bookings?pageSize=100') });
export const useBookedTickets = (search = '') =>
  useQuery({ queryKey: ['booked-tickets', search], queryFn: page<BookedTicket>(`/booked-tickets?pageSize=50&search=${encodeURIComponent(search)}`) });
export const useAvailability = () => useQuery({ queryKey: ['availability'], queryFn: list<Availability>('/availability') });

// ── tasks ──
export const useTasks = () => useQuery({ queryKey: ['tasks'], queryFn: page<Task>('/tasks?pageSize=100') });
export const useDepartments = () => useQuery({ queryKey: ['departments'], queryFn: list<Department>('/departments') });
export function useToggleTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (t: Task) => apiPatch<Task>(`/tasks/${t.id}`, { done: !t.done }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; priority?: string; tag?: string; assigneeId?: string; departmentId?: string; officeKey?: string; dueAt?: string; description?: string; recurrence?: string }) => apiPost<Task>('/tasks', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
export interface TaskPatch {
  title?: string; description?: string; priority?: string; dueAt?: string | null;
  tag?: string; assigneeId?: string | null; departmentId?: string | null; officeKey?: string | null;
  recurrence?: string | null; done?: boolean;
}
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: TaskPatch & { id: string }) => apiPatch<Task>(`/tasks/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
export function useAddSubtask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, title }: { taskId: string; title: string }) => apiPost(`/tasks/${taskId}/subtasks`, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
export function useToggleSubtask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, subtaskId, done }: { taskId: string; subtaskId: string; done: boolean }) =>
      apiPatch(`/tasks/${taskId}/subtasks/${subtaskId}`, { done }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
export function useDeleteSubtask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, subtaskId }: { taskId: string; subtaskId: string }) => apiDelete(`/tasks/${taskId}/subtasks/${subtaskId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

// ── comms ──
export const useTicketCategories = () => useQuery({ queryKey: ['ticket-categories'], queryFn: list<TicketCategory>('/tickets/categories') });
export const useTickets = () => useQuery({ queryKey: ['tickets'], queryFn: page<Ticket>('/tickets?pageSize=100') });
export const useTicket = (id: string | null) =>
  useQuery({ queryKey: ['ticket', id], queryFn: () => apiGet<Ticket>(`/tickets/${id}`), enabled: !!id });
export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { categoryId: string; title: string; body: string; priority?: string }) => apiPost<Ticket>('/tickets', b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}
export const useInfostaff = () => useQuery({ queryKey: ['infostaff'], queryFn: page<Infostaff>('/infostaff?pageSize=50') });
export function useCreateInfostaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; body: string; category?: string; urgent?: boolean; courseId?: string; audience?: string; sendEmail?: boolean }) => apiPost<Infostaff>('/infostaff', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['infostaff'] }),
  });
}
export function useMarkInfostaffRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/infostaff/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['infostaff'] }),
  });
}
export const useNotifications = () => useQuery({ queryKey: ['notifications'], queryFn: page<Notification>('/notifications?pageSize=30') });
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ── org ──
export const useMeetings = () => useQuery({ queryKey: ['meetings'], queryFn: page<Meeting>('/meetings?pageSize=100') });
export interface MeetingInput { title: string; date: string; start: string; end: string; location?: string; description?: string; attendees?: string[]; color?: string; meetingUrl?: string }
export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MeetingInput) => apiPost<Meeting>('/meetings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}
export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: MeetingInput & { id: string }) => apiPatch<Meeting>(`/meetings/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}
export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/meetings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}
export const useShifts = () => useQuery({ queryKey: ['shifts'], queryFn: list<Shift>('/shifts') });
export const useShiftTypes = () => useQuery({ queryKey: ['shift-types'], queryFn: list<ShiftType>('/shifts/types') });
export const useShiftLocations = () => useQuery({ queryKey: ['shift-locations'], queryFn: list<ShiftLocation>('/shifts/locations') });
export interface ShiftLocationInput { key: string; label: string; short?: string; color?: string; autoAssign?: boolean }
export function useCreateShiftLocation() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: ShiftLocationInput) => apiPost<ShiftLocation>('/shifts/locations', body), onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-locations'] }) });
}
export function useUpdateShiftLocation() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ key, ...body }: Partial<ShiftLocationInput> & { key: string }) => apiPatch<ShiftLocation>(`/shifts/locations/${key}`, body), onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-locations'] }) });
}
export function useDeleteShiftLocation() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (key: string) => apiDelete(`/shifts/locations/${key}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-locations'] }) });
}
// ── roster planning: contracts, preferences, staffing needs, auto-generation ──
export const useRosterBalances = (from: string, to: string) =>
  useQuery({ queryKey: ['roster-balances', from, to], queryFn: () => apiGet<RosterBalance[]>(`/roster/balances?from=${from}&to=${to}`) });
export const useShiftPreferences = () =>
  useQuery({ queryKey: ['roster-preferences'], queryFn: list<ShiftPreference>('/roster/preferences') });
export const useStaffingTargets = () =>
  useQuery({ queryKey: ['roster-targets'], queryFn: list<StaffingTarget>('/roster/targets') });

export type ShiftPreferenceInput = Omit<ShiftPreference, 'employeeId' | 'note'> & { note?: string | null };
export function useSavePreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, ...body }: ShiftPreferenceInput & { employeeId: string }) =>
      apiPut<ShiftPreference>(`/roster/preferences/${employeeId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roster-preferences'] }),
  });
}
export function useSaveTargets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targets: StaffingTarget[]) => apiPut<StaffingTarget[]>('/roster/targets', { targets }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roster-targets'] }),
  });
}
/** Preview by default; pass `apply` to write the proposal onto the roster. */
export function useGenerateRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: string; to: string; startTime?: string; endTime?: string; apply?: boolean }) =>
      apiPost<RosterPlan>('/roster/generate', body),
    onSuccess: (result) => {
      if (!result.applied) return;
      void qc.invalidateQueries({ queryKey: ['shifts'] });
      void qc.invalidateQueries({ queryKey: ['roster-balances'] });
    },
  });
}

export function useUpsertShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { employeeId: string; date: string; typeKey?: string | null; locationKey?: string | null; range?: string | null; segments?: unknown[]; note?: string }) => apiPut<Shift>('/shifts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
}

// ── office handovers (end-of-day notes) ──
export const useHandovers = (opts: { officeKey?: string; from?: string; to?: string } = {}) => {
  const qs = new URLSearchParams();
  if (opts.officeKey) qs.set('officeKey', opts.officeKey);
  if (opts.from) qs.set('from', opts.from);
  if (opts.to) qs.set('to', opts.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: ['handovers', suffix],
    queryFn: list<OfficeHandover>(`/handovers${suffix}`),
  });
};
export function useUpsertHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { officeKey: string; date: string; body: string }) => apiPut<OfficeHandover>('/handovers', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['handovers'] }),
  });
}
export function useDeleteHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/handovers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['handovers'] }),
  });
}

// ── directory ──
export const useEmployees = () => useQuery({ queryKey: ['employees'], queryFn: page<Employee>('/employees?pageSize=100') });
export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; name: string; password?: string; roles?: string[]; role?: string; departmentId?: string }) =>
      apiPost<Employee & { inviteEmail?: { sent: boolean; reason?: string } }>('/employees', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}
export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; roles?: string[]; active?: boolean; password?: string; role?: string; departmentId?: string | null; weeklyHours?: number | null; leaveDaysYear?: number | null }) => apiPatch<Employee>(`/employees/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      // Contract changes move the roster's hour warnings.
      void qc.invalidateQueries({ queryKey: ['roster-balances'] });
    },
  });
}
export const useCompanyEmails = () => useQuery({ queryKey: ['company-emails'], queryFn: page<CompanyEmail>('/company-emails?pageSize=100') });
type CompanyEmailInput = { section: string; name: string; email: string; role?: string; phone?: string; notes?: string };
export function useCreateCompanyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompanyEmailInput) => apiPost<CompanyEmail>('/company-emails', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company-emails'] }),
  });
}
export function useUpdateCompanyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<CompanyEmailInput> & { id: string }) => apiPatch<CompanyEmail>(`/company-emails/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company-emails'] }),
  });
}
export function useDeleteCompanyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/company-emails/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company-emails'] }),
  });
}

// ── knowledge ──
export const useWiki = () => useQuery({ queryKey: ['wiki'], queryFn: list<WikiPage>('/wiki') });
export function useCreateWikiPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slug: string; title: string; body?: string; icon?: string; parentId?: string; tags?: string[]; isFolder?: boolean; ownerId?: string }) =>
      apiPost<WikiPage>('/wiki', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki'] }),
  });
}
export function useUpdateWikiPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; title?: string; body?: string; icon?: string; tags?: string[]; starred?: boolean; parentId?: string | null; ownerId?: string; versionMessage?: string }) =>
      apiPatch<WikiPage>(`/wiki/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki'] }),
  });
}
/** Persist a drag & drop: new parent and sort position for each moved page. */
export function useReorderWiki() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; parentId: string | null; order: number }[]) =>
      apiPatch<{ moved: number }>('/wiki/reorder', { items }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki'] }),
  });
}

// ── learning ──
const invalidateLearning = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ['courses'] });
  void qc.invalidateQueries({ queryKey: ['my-courses'] });
  void qc.invalidateQueries({ queryKey: ['course'] });
  void qc.invalidateQueries({ queryKey: ['course-stats'] });
  void qc.invalidateQueries({ queryKey: ['learning-overview'] });
};

export type CourseInput = {
  title: string; icon?: string; color?: string; category?: string; description?: string;
  mandatory?: boolean; published?: boolean; openToAll?: boolean;
  passingScore?: number; durationMin?: number; cover?: string; lessons?: Lesson[];
};

export const useCourses = () => useQuery({ queryKey: ['courses'], queryFn: list<LearningCourse>('/learning/courses') });
export const useMyCourses = () => useQuery({ queryKey: ['my-courses'], queryFn: list<LearningAssignment & { course: LearningCourse }>('/learning/my') });
export const useCourse = (id: string | null) => useQuery({
  queryKey: ['course', id], enabled: !!id,
  queryFn: () => apiGet<LearningCourse>(`/learning/courses/${id}`),
});
export const useCourseStats = (id: string | null) => useQuery({
  queryKey: ['course-stats', id], enabled: !!id,
  queryFn: () => apiGet<CourseStats>(`/learning/courses/${id}/stats`),
});
export const useLearningOverview = (enabled = true) => useQuery({
  queryKey: ['learning-overview'], enabled,
  queryFn: () => apiGet<LearningOverview>('/learning/overview'),
});

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CourseInput) => apiPost<LearningCourse>('/learning/courses', body),
    onSuccess: () => invalidateLearning(qc),
  });
}
export function useUpdateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<CourseInput> & { id: string }) => apiPatch<LearningCourse>(`/learning/courses/${id}`, body),
    onSuccess: () => invalidateLearning(qc),
  });
}
export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/learning/courses/${id}`), onSuccess: () => invalidateLearning(qc) });
}
export function useEnrol() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (courseId: string) => apiPost<LearningAssignment>(`/learning/courses/${courseId}/enrol`), onSuccess: () => invalidateLearning(qc) });
}
export function useCompleteLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, lessonId, done }: { courseId: string; lessonId: string; done?: boolean }) =>
      apiPost<LearningAssignment>(`/learning/courses/${courseId}/lessons/${lessonId}/complete`, { done: done ?? true }),
    onSuccess: () => invalidateLearning(qc),
  });
}
export function useSubmitQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, lessonId, answers }: { courseId: string; lessonId: string; answers: number[] }) =>
      apiPost<QuizResult>(`/learning/courses/${courseId}/lessons/${lessonId}/quiz`, { answers }),
    onSuccess: () => invalidateLearning(qc),
  });
}
export function useAssignCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { courseId: string; employeeIds?: string[]; departmentIds?: string[]; everyone?: boolean; dueAt?: string }) =>
      apiPost<{ assigned: number }>('/learning/assignments', body),
    onSuccess: () => invalidateLearning(qc),
  });
}
export function useRemoveAssignment() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/learning/assignments/${id}`), onSuccess: () => invalidateLearning(qc) });
}

// ── own profile (nickname is private to the owner) ──
export const useMyProfile = () => useQuery({ queryKey: ['my-profile'], queryFn: () => apiGet<MyProfile>('/employees/me') });
export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { nickname?: string; avatarUrl?: string }) => apiPatch<MyProfile>('/employees/me', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-profile'] }),
  });
}
export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) => apiPatch<{ ok: boolean }>('/employees/me/password', body),
  });
}

// ── customer service ──
export const useConversations = () => useQuery({ queryKey: ['cs-conversations'], queryFn: page<CsConversation>('/cs/conversations?pageSize=100') });

// ── admin ──
export const useRoles = () => useQuery({ queryKey: ['roles'], queryFn: list<AppRole>('/admin/roles') });
export const useAudit = () => useQuery({ queryKey: ['audit'], queryFn: page<AuditLog>('/admin/audit?pageSize=50') });
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; name: string; description?: string; color?: string; permissions: Record<string, boolean> }) => apiPost<AppRole>('/admin/roles', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}
export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string; color?: string; permissions?: Record<string, boolean> }) => apiPatch<AppRole>(`/admin/roles/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

// ── departments (admin management) ──
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: string; name: string; short?: string; color?: string; description?: string }) => apiPost<Department>('/departments', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  });
}
export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; short?: string; color?: string; description?: string }) => apiPatch<Department>(`/departments/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  });
}
export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/departments/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['departments'] }); qc.invalidateQueries({ queryKey: ['employees'] }); },
  });
}

// ── vault ──
export const useVault = () => useQuery({ queryKey: ['vault'], queryFn: page<VaultEntryView>('/vault?pageSize=100') });
export const useRevealSecret = () => useMutation({ mutationFn: (id: string) => apiPost<{ secret: string }>(`/vault/${id}/reveal`) });

export { useQueryClient, apiDelete };

// ── OAuth / OIDC client apps (admin) ──
export interface OAuthClientView {
  id: string; clientId: string; name: string; redirectUris: string[];
  allowedScopes: string[]; trusted: boolean; confidential: boolean;
  logoutUri: string | null; createdAt: string;
}
export const useOAuthClients = () => useQuery({ queryKey: ['oauth-clients'], queryFn: list<OAuthClientView>('/oauth/clients') });
export function useCreateOAuthClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; redirectUris: string[]; allowedScopes?: string[]; confidential?: boolean; trusted?: boolean; logoutUri?: string }) =>
      apiPost<OAuthClientView & { clientSecret: string | null }>('/oauth/clients', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-clients'] }),
  });
}
export function useUpdateOAuthClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; redirectUris?: string[]; allowedScopes?: string[]; trusted?: boolean; logoutUri?: string }) =>
      apiPatch<OAuthClientView>(`/oauth/clients/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-clients'] }),
  });
}
export function useRotateOAuthSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ clientSecret: string }>(`/oauth/clients/${id}/rotate-secret`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-clients'] }),
  });
}
export function useDeleteOAuthClient() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/oauth/clients/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-clients'] }) });
}

// ── personal notes (private per-user) ──
export interface PersonalNote { id: string; title: string; body: string; color: string | null; pinned: boolean; order: number; folderId: string | null; updatedAt: string; }
export const useNotes = () => useQuery({ queryKey: ['notes'], queryFn: list<PersonalNote>('/notes') });
export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: { title?: string; body?: string; color?: string | null; pinned?: boolean; folderId?: string | null }) => apiPost<PersonalNote>('/notes', body), onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }) });
}
export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, ...body }: { id: string; title?: string; body?: string; color?: string | null; pinned?: boolean; folderId?: string | null; order?: number }) => apiPatch<PersonalNote>(`/notes/${id}`, body), onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }) });
}
export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/notes/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }) });
}

// ── learning compliance (overdue mandatory) ──
export interface ComplianceRow {
  assignmentId: string;
  employee: { id: string; name: string; email: string; initials: string | null; departmentId: string | null } | null;
  course: { id: string; title: string; icon: string | null; color: string | null };
  progress: number; dueAt: string | null; daysLate: number;
}
export interface Compliance { totalOverdue: number; peopleAffected: number; overdue: ComplianceRow[]; }
export const useCompliance = (enabled = true) => useQuery({ queryKey: ['learning-compliance'], enabled, queryFn: () => apiGet<Compliance>('/learning/compliance') });

// ── leave (ferie / permessi) ──
export interface LeaveRequest {
  id: string; employeeId: string; type: 'ferie' | 'permesso' | 'malattia';
  startDate: string; endDate: string; partial: boolean;
  startTime: string | null; endTime: string | null; reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewedByName: string | null; reviewedAt: string | null; reviewNote: string | null;
  createdAt: string;
  employee?: { id: string; name: string; initials: string | null; departmentId: string | null } | null;
}
export const useLeaves = (params: { scope?: 'mine' | 'all'; status?: string } = {}) => {
  const qs = new URLSearchParams(params as Record<string, string>).toString();
  return useQuery({ queryKey: ['leaves', params], queryFn: () => apiGet<LeaveRequest[]>(`/leaves${qs ? `?${qs}` : ''}`) });
};
export const useApprovedLeaves = (from: string, to: string) =>
  useQuery({ queryKey: ['leaves-approved', from, to], queryFn: () => apiGet<LeaveRequest[]>(`/leaves/approved?from=${from}&to=${to}`) });
const invalidateLeaves = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ['leaves'] });
  void qc.invalidateQueries({ queryKey: ['leaves-approved'] });
};
export function useCreateLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: string; startDate: string; endDate: string; partial?: boolean; startTime?: string; endTime?: string; reason?: string }) => apiPost<LeaveRequest>('/leaves', body),
    onSuccess: () => invalidateLeaves(qc),
  });
}
export function useReviewLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reviewNote }: { id: string; status: 'approved' | 'rejected'; reviewNote?: string }) => apiPatch<LeaveRequest>(`/leaves/${id}/review`, { status, reviewNote }),
    onSuccess: () => invalidateLeaves(qc),
  });
}
export function useDeleteLeave() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/leaves/${id}`), onSuccess: () => invalidateLeaves(qc) });
}

// ── note folders ──
export interface NoteFolder { id: string; name: string; order: number; parentId: string | null; }
export const useNoteFolders = () => useQuery({ queryKey: ['note-folders'], queryFn: list<NoteFolder>('/notes/folders') });
export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ name, parentId }: { name: string; parentId?: string | null }) => apiPost<NoteFolder>('/notes/folders', { name, parentId }), onSuccess: () => qc.invalidateQueries({ queryKey: ['note-folders'] }) });
}
export function useUpdateFolder() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, ...body }: { id: string; name?: string; parentId?: string | null }) => apiPatch<NoteFolder>(`/notes/folders/${id}`, body), onSuccess: () => qc.invalidateQueries({ queryKey: ['note-folders'] }) });
}
export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/notes/folders/${id}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['note-folders'] }); void qc.invalidateQueries({ queryKey: ['notes'] }); } });
}
